import useIdentity from "@/features/ui/hooks/use-identity";
import { useMemo } from "react";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import { PersistedOrderSheetSetListQuery } from "@/features/order-status/agency-order-sheet-set-list-page";
import {
  orderStatusOptionList,
  remittanceRequiredStatusOptionList,
} from "@/shared/constants/status";
import { convertOrderStatusToNew } from "@/shared/utils";

import { format, set, subDays, subMonths, subYears } from "date-fns";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

type TrendingPeriodValue =
  | "7 days"
  | "1 month"
  | "3 months"
  | "6 months"
  | "1 year";

/** DST/타임존 튐 줄이기: 오늘 '정오' 기준 */
function todayNoon() {
  return set(new Date(), {
    hours: 12,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
}

/** period -> { start, end } */
function calcPeriodRange(period: TrendingPeriodValue) {
  const end = todayNoon();
  const start =
    period === "7 days"
      ? subDays(end, 7)
      : period === "1 month"
      ? subMonths(end, 1)
      : period === "3 months"
      ? subMonths(end, 3)
      : period === "6 months"
      ? subMonths(end, 6)
      : subYears(end, 1);

  return { start, end };
}

export const useBuyerOrderSheetSetChipList = (
  query: PersistedOrderSheetSetListQuery
) => {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();

  const brandKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      const request: ListAllCompaniesRequest = {
        type__in: ["BRAND"],
      };
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const supplierKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      const request: ListAllCompaniesRequest = {
        type__in: ["BOUTIQUE"],
      };
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const { data: brandData } = useListAllCompanies(
    brandKey?.parameter,
    brandKey?.request
  );

  const { data: supplierData } = useListAllCompanies(
    supplierKey?.parameter,
    supplierKey?.request
  );

  const brandList = [
    ...(query["madeByCompany.id__in"]
      ?.map((id) => brandData?.find((item) => item.id === +id))
      ?.map((brand) => ({
        id: brand?.id,
        name: brand?.name,
        type: "LINE_SHEET_BRAND" as const,
      })) ?? []),
  ].filter((item) => item.id);

  // const typeQuery = [
  //   ...(query["lineSheetSet.type__in"]?.map((types) => ({
  //     id: types,
  //     name: types,
  //     types: "LINE_SHEET_TYPE" as const,
  //   })) ?? []),
  //   ...(query["lineSheetSet.type__eq"]
  //     ? [
  //         {
  //           id: query["lineSheetSet.type__eq"],
  //           name: query["lineSheetSet.type__eq"],
  //           types: "LINE_SHEET_TYPE_EQ" as const,
  //         },
  //       ]
  //     : []),
  // ];

  const supplierList = [
    ...(query["lineSheetSet.issuedByCompany.id__in"]
      ?.map((id) => supplierData?.find((item) => item.id === +id))
      ?.map((supplier) => ({
        id: supplier?.id,
        name: supplier?.name,
        type: "LINE_SHEET_SUPPLIER" as const,
      })) ?? []),
  ].filter((item) => item.id);

  // ✅ status__in chip
  const statusList = [
    ...(query["status__in"]?.map((status) => ({
      id: status,
      name: tTitle(
        "order_history:" +
          orderStatusOptionList
            .find((option) => option.value === convertOrderStatusToNew(status))
            ?.name.toLowerCase()
      ),
      type: "ORDER_SHEET_STATUS" as const,
    })) ?? []),
  ].filter((item) => item.name);

  // ✅ unremittedPaymentType__in chip
  const remittanceRequiredList = [
    ...(query["unremittedPaymentType__in"]?.map((type) => ({
      id: type,
      name: tTitle(
        `order_history:invoice.remittance_required_category.${
          remittanceRequiredStatusOptionList.find(
            (option) => option.value === type
          )?.name
        }`
      ),
      type: "REMITTANCE_REQUIRED" as const,
    })) ?? []),
  ].filter((item) => item.name);

  // ✅ period 날짜 chip 추가 (표시: YYYY-MM-DD ~ YYYY-MM-DD)
  // query에 period가 저장된다는 전제: query.period
  const periodChip = useMemo(() => {
    const period = query.period as TrendingPeriodValue | undefined;
    if (!period) return [];

    const { start, end } = calcPeriodRange(period);
    const label = `${format(start, "yyyy-MM-dd")} ~ ${format(
      end,
      "yyyy-MM-dd"
    )}`;

    return [
      {
        id: period,
        name: label,
        type: "PERIOD" as const,
      },
    ];
  }, [query.period]);

  const chipList = [
    ...brandList,
    ...supplierList,
    // ...typeQuery,
    ...statusList,
    ...remittanceRequiredList,
    ...periodChip,
  ].filter((item) => item && item.name);

  const refineQuery = (type?: string, id?: number | string) => {
    if (type === "LINE_SHEET_BRAND") {
      return {
        "madeByCompany.id__in": (query["madeByCompany.id__in"] || []).filter(
          (brandId) => +brandId !== +id!
        ),
      };
    } else if (type === "LINE_SHEET_SUPPLIER") {
      return {
        "lineSheetSet.issuedByCompany.id__in": (
          query["lineSheetSet.issuedByCompany.id__in"] || []
        ).filter((companyId) => +companyId !== +id!),
      };
    }
    // else if (types === "LINE_SHEET_TYPE") {
    //   return {
    //     "lineSheetSet.type__in": (query["lineSheetSet.type__in"] || []).filter(
    //       (t) => t !== id
    //     ),
    //   };
    // }
    // else if (types === "LINE_SHEET_TYPE_EQ") {
    //   return {
    //     "lineSheetSet.type__eq": undefined,
    //   };
    // }
    else if (type === "ORDER_SHEET_STATUS") {
      return {
        status__in: (query["status__in"] || []).filter(
          (s) => String(s) !== String(id)
        ),
      };
    } else if (type === "REMITTANCE_REQUIRED") {
      return {
        unremittedPaymentType__in: (
          query["unremittedPaymentType__in"] || []
        ).filter((t) => String(t) !== String(id)),
      };
    } else if (type === "PERIOD") {
      return {
        period: undefined,
      };
    }
  };

  return {
    refineQuery,
    chipList,
  };
};
